/**
 * /learn/courses/:courseId/sections          — sections grid
 * /learn/courses/:courseId/sections/:sectionId — same view, sectionId acts
 *                                                as a focus marker so the
 *                                                "Continue course" CTA can
 *                                                land on a deep link.
 *
 * Matches Figma 9173:279035: big purple course title, centred progress bar,
 * PreviewTileGrid of sections inside the gradient frame, centred orange
 * "Go back" pill at the bottom.
 *
 * Tile state is derived from per-module progress:
 *   - all modules completed → 'finished'
 *   - the section containing the cursor → 'open'
 *   - everything past the cursor → 'locked' ("Finish The Previous Section…")
 *   - completed course (review mode) → all sections 'open'
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useImageUrl } from '../../services/uploads';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import { PreviewList, default as PreviewListItem } from '../../components/programs/preview/PreviewList';
import { BackPill } from '../../components/programs/preview/PreviewWelcomeHero';
import LearnerCourseProgressBar from '../../components/learner/LearnerCourseProgressBar';
import StickyProgressRow from '../../components/learner/StickyProgressRow';
import { getEnrolmentAvailability, isEnrolmentLocked } from '../../utils/enrolmentAvailability';
import type { components } from '../../types/learnerApi';

type Section = components['schemas']['Section'];
type ContentModule = components['schemas']['ContentModule'];
type EvaluationModule = components['schemas']['EvaluationModule'];
type FeedbackModule = components['schemas']['FeedbackModule'];
type AnyModule = ContentModule | EvaluationModule | FeedbackModule;

function moduleStatus(m: AnyModule): 'not_started' | 'in_progress' | 'completed' | undefined {
  return (m as ContentModule).progress?.status as
    | 'not_started'
    | 'in_progress'
    | 'completed'
    | undefined;
}

function sortModules(modules: AnyModule[]): AnyModule[] {
  return modules.slice().sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
}

const CourseSectionsPage: React.FC = () => {
  const { assignmentId } = useParams<{
    assignmentId?: string;
    sectionId?: string;
  }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const routes = useCourseRoutes(id);
  const { data: course, isLoading, isError } = useCourse(id);

  // Date-window gate: bounce deep-links back to the detail page when the
  // enrolment hasn't opened yet or has already ended. The detail page owns
  // the locked-state UI.
  useEffect(() => {
    if (!course) return;
    const availability = getEnrolmentAvailability(course.assignment, {
      completed: course.progress?.bucket === 'completed',
    });
    if (isEnrolmentLocked(availability)) {
      navigate(routes.base, { replace: true });
    }
  }, [course, navigate, routes.base]);

  const sortedSections = useMemo<Section[]>(() => {
    return (course?.sections ?? [])
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [course]);

  const cursorSectionIdx = useMemo(() => {
    let firstNotStartedIdx = -1;
    for (let i = 0; i < sortedSections.length; i++) {
      const sortedModules = sortModules((sortedSections[i].modules ?? []) as AnyModule[]);
      for (const m of sortedModules) {
        const status = moduleStatus(m);
        if (status === 'in_progress') return i;
        if (firstNotStartedIdx < 0 && (!status || status === 'not_started')) {
          firstNotStartedIdx = i;
        }
      }
    }
    return firstNotStartedIdx;
  }, [sortedSections]);

  if (isLoading) return <SectionsPageSkeleton />;

  if (isError || !course) {
    return (
      <div className="w-[95%] sm:w-[90%] mx-auto py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't load this course.</p>
        <Link to={routes.coursesIndex} className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to My Courses
        </Link>
      </div>
    );
  }

  const courseCompleted = course.progress?.bucket === 'completed';

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 sm:space-y-8"
    >
      {/* Big purple course title */}
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary-500 leading-[1.05] tracking-tight px-1">
        {course.title ?? 'Course'}
      </h1>

      {/* Back pill + progress bar on the same row — pinned while scrolling. */}
      <StickyProgressRow>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-x-8 px-1">
          <BackPill
            label="Go back"
            onClick={() => navigate(routes.base)}
          />
          {course.progress && (
            <div className="flex-1 min-w-[220px]">
              <LearnerCourseProgressBar
                percent={course.progress.progress_percent ?? 0}
                modulesCompleted={course.progress.modules_completed ?? 0}
                modulesTotal={course.progress.modules_total ?? 0}
              />
            </div>
          )}
        </div>
      </StickyProgressRow>

      {/* Sections — cards grid or a table-of-contents list, per course layout */}
      {sortedSections.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">
          This course doesn't have any sections yet.
        </div>
      ) : course?.layout === 'list' ? (
        <PreviewList>
          {sortedSections.map((section, i) => (
            <SectionListCell
              key={section.id}
              section={section}
              index={i}
              cursorSectionIdx={cursorSectionIdx}
              courseCompleted={courseCompleted}
              onOpen={() => {
                if (section.id) {
                  navigate(`${routes.base}/sections/${section.id}/modules`);
                }
              }}
            />
          ))}
        </PreviewList>
      ) : (
        <PreviewTileGrid maxColumns={4}>
          {sortedSections.map((section, i) => (
            <SectionTileCell
              key={section.id}
              section={section}
              index={i}
              cursorSectionIdx={cursorSectionIdx}
              courseCompleted={courseCompleted}
              reduceMotion={!!reduceMotion}
              onOpen={() => {
                if (section.id) {
                  navigate(`${routes.base}/sections/${section.id}/modules`);
                }
              }}
            />
          ))}
        </PreviewTileGrid>
      )}
    </motion.div>
  );
};

export default CourseSectionsPage;

interface SectionTileCellProps {
  section: Section;
  index: number;
  cursorSectionIdx: number;
  courseCompleted: boolean;
  reduceMotion: boolean;
  onOpen: () => void;
}

function SectionTileCell({
  section,
  index,
  cursorSectionIdx,
  courseCompleted,
  reduceMotion,
  onOpen,
}: SectionTileCellProps) {
  const sortedModules = useMemo(
    () => sortModules((section.modules ?? []) as AnyModule[]),
    [section.modules]
  );

  const allCompleted =
    sortedModules.length > 0 && sortedModules.every((m) => moduleStatus(m) === 'completed');

  // Resolve the section's S3 key into a presigned URL for the tile cover.
  const [coverUrl] = useImageUrl(section.image_url ?? null);

  const state: 'open' | 'locked' | 'finished' = useMemo(() => {
    if (allCompleted) return 'finished';
    if (sortedModules.length === 0) return 'locked';
    if (courseCompleted) return 'open';
    if (cursorSectionIdx < 0) return 'locked';
    if (index < cursorSectionIdx) return 'finished';
    if (index === cursorSectionIdx) return 'open';
    return 'locked';
  }, [allCompleted, sortedModules.length, courseCompleted, cursorSectionIdx, index]);

  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={section.title ?? `Section ${index + 1}`}
      state={state}
      lockedScope="Section"
      entranceDelay={reduceMotion ? 0 : index * 0.05}
      onClick={state === 'locked' ? undefined : onOpen}
    />
  );
}

/** List-layout row — same gating as SectionTileCell, rendered as a TOC row. */
function SectionListCell({
  section,
  index,
  cursorSectionIdx,
  courseCompleted,
  onOpen,
}: Omit<SectionTileCellProps, 'reduceMotion'>) {
  const sortedModules = useMemo(
    () => sortModules((section.modules ?? []) as AnyModule[]),
    [section.modules]
  );
  const allCompleted =
    sortedModules.length > 0 && sortedModules.every((m) => moduleStatus(m) === 'completed');

  const state: 'open' | 'locked' | 'finished' = useMemo(() => {
    if (allCompleted) return 'finished';
    if (sortedModules.length === 0) return 'locked';
    if (courseCompleted) return 'open';
    if (cursorSectionIdx < 0) return 'locked';
    if (index < cursorSectionIdx) return 'finished';
    if (index === cursorSectionIdx) return 'open';
    return 'locked';
  }, [allCompleted, sortedModules.length, courseCompleted, cursorSectionIdx, index]);

  return (
    <PreviewListItem
      imageUrl={section.image_url ?? null}
      kicker={`Section ${index + 1}`}
      index={index}
      title={section.title ?? `Section ${index + 1}`}
      state={state}
      lockedScope="section"
      entranceDelay={index * 0.04}
      onClick={onOpen}
    />
  );
}

function SectionsPageSkeleton() {
  return (
    <div className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 animate-pulse">
      <div className="h-12 w-2/3 rounded bg-text-tertiary/10" />
      <div className="h-[58px] max-w-[420px] mx-auto rounded-full bg-text-tertiary/10" />
      <div className="rounded-[28px] p-3 bg-text-tertiary/5">
        <div className="rounded-[20px] bg-background-primary p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-[2/3] rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    </div>
  );
}
